import { EmberClueType, isEmberClueType, TutorialType } from "game-common/models"
import { Container, Graphics, Point, Rectangle } from "pixi.js"
import { DropShadowFilter } from "pixi-filters"
import { getUiMetrics } from "./ui_metrics"

import { GuiManager } from "../gui_manager"
import { CLOSE_BUTTON_RADIUS, CloseButton } from "./close_button"
import { DyanmicTextContainer } from "./dynamic_text_container"
import {
    clueBoxLayout,
    CLUE_BOX_MIN_X,
    CLUE_BOX_MIN_Y,
    CLUE_LEADER_GAP,
    CLUE_TEXT,
    CluePlacement,
    EmberClueDirector,
    routeTutorial,
} from "./ember_clues"
import { Fader } from "./fader"
import { Throbber } from "./throbber"
import { Callback } from "game-common/util"

// The dismiss button is the SAME control every dialog is closed by (gui/close_button.ts), just
// smaller: same disc, rim, stroked X and hover states, so there is one close affordance in the game
// rather than a second hand-drawn one that has to be kept looking like the first. Scaling the shared
// radius rather than hardcoding one keeps its bigger touch size on a phone.
const DISMISS_RADIUS = Math.round(CLOSE_BUTTON_RADIUS * 0.75)
const DISMISS_GAP = 6 // clear air between the last line of text and the button row

// The one red the clues are drawn in: the ring round the control, the leader, and the box's outline.
const CLUE_RED = 0xff0000
// How far the leader starts inside the panel, so the join is never a hairline short.
const LEADER_OVERLAP = 3

// How long an overworld popup stays up. Tuned for a notice nobody is waiting on: it says its piece
// and gets out of the way. (A clue's much longer lifetime lives in ember_clues.ts, with the rest of
// the clue bookkeeping.)
const POPUP_TIMEOUT_MS = 7000

// The control bar's inventory button, as the existing controlbar popup boxes it: the first entry in
// ControlBar.setup() (inventory.png), measured from the bar's own x in the gui surface.
const CONTROLBAR_ITEMS_OFFSET_X = 15
const CONTROLBAR_ITEMS_W = 32
const CONTROLBAR_ITEMS_H = 30

// Where a clue points, handed over by whatever drew the control.
//
// The rect is in `host` coordinates and the clue is drawn INTO `host` -- not into the gui surface --
// because most of these controls live inside the inventory modal, which is a separate container on
// the stage. Drawing into the target's own container is what keeps a clue in the right coordinate
// space and on the right side of the z-order, and is the same trick setupCompanionTutorial uses to
// hang its box off an entity's renderable.
export interface ClueAnchor {
    host: Container
    x: number
    y: number
    w: number
    h: number
    // Which side of the control the box goes on. Defaults to "left", which suits the top-right HUD row
    // and the docked panel. The ready bar is in the bottom-left corner with nothing to its left, so it
    // asks for "above" -- clamped beside it, the box landed on top of the thing it was pointing at.
    placement?: CluePlacement
    // Left edge the text box may not cross, in `host` coordinates. Defaults to a small margin, which
    // is right for a host that is the whole screen; a right-docked panel's container starts hundreds
    // of pixels in, so its clues legitimately sit at a negative x and pass a negative bound.
    boxMinX?: number
    boxMaxX?: number
    boxMinY?: number
    boxMaxY?: number
}

// A HudChip, structurally. Typed rather than imported so this file doesn't depend on the chip (and so
// a test can hand in a stub).
interface ClueChip {
    visible: boolean
    x: number
    y: number
    getWidth: () => number
    getHeight: () => number
    parent?: Container
}

// The ready bar, by SHAPE rather than as the EquippedWidget it always is in the game (gui/equipped.ts),
// for the same reason ClueChip above is structural: it keeps this file off the sprite graph. The wood
// slot is optional because only the real widget tracks one -- a bar with no wood readied, and any
// stand-in for it, simply has no slot to point at, which builtInClueAnchor already treats as "nothing
// to anchor to".
type ReadyBar = Graphics & { getWoodSlotBounds?: () => Rectangle | undefined }

export class Tutorial {
    gui: GuiManager
    surface: Graphics
    equippedWidget: ReadyBar
    controlBar: Graphics
    minimapSurface: Graphics
    // The camp's stand-in for the control bar's inventory button (ember rooms suppress the bar), and
    // therefore what the first clue points at. Optional so a test can leave it out.
    emberInventoryChip?: ClueChip
    activeTutorials: Set<TutorialType> = new Set()
    // Per-room suppression latch (set from GuiManager.roomUpdated via WorldState.visibility). When
    // true, setup() is a no-op so no tutorial popups appear — ember rooms suppress them. It does NOT
    // hold back the ember clues: see setup().
    suppressed: boolean = false

    // The ember clues' bookkeeping (armed vs anchored, expiry, teardown) lives next door in
    // ember_clues.ts, free of pixi; all this file provides is the drawing.
    private clues: EmberClueDirector<ClueAnchor> = new EmberClueDirector<ClueAnchor>({
        draw: (tutorialType, anchor) => this.drawClue(tutorialType, anchor),
        builtInAnchor: tutorialType => this.builtInClueAnchor(tutorialType) || undefined,
    })

    constructor(
        gui: GuiManager,
        surface: Graphics,
        equippedWidget: ReadyBar,
        controlBar: Graphics,
        minimapSurface: Graphics,
        emberInventoryChip?: ClueChip,
    ) {
        this.gui = gui
        this.surface = surface
        this.equippedWidget = equippedWidget
        this.controlBar = controlBar
        this.minimapSurface = minimapSurface
        this.emberInventoryChip = emberInventoryChip
    }

    // The dismiss button sits INSIDE the panel, on its own row under the text, the way the chat
    // bubble's "Next" button does: the container flows it like any other row, so the backdrop grows to
    // enclose it instead of having a circle hung off the top-left corner where it overlapped both the
    // panel edge and the text.
    //
    // CloseButton draws itself CENTRED ON ITS OWN ORIGIN (it is built to hang off a panel corner), but
    // append2 flows children by their top-left, so both offsets below carry a radius to put the disc
    // where a top-left-origin child of the same size would have gone.
    addDismiss = (container: DyanmicTextContainer, callback: Callback<void>) => {
        const radius = DISMISS_RADIUS
        // Measure the text block BEFORE the button joins it, or it right-aligns against itself.
        const textWidth = container.width

        const dismissButton = new CloseButton({ radius, onClick: callback })

        container.append2(dismissButton, {
            // Math.max keeps it on the panel when the text is narrower than the button. A leftSpacer of
            // 0 is falsy and would be ignored, which is the same place the flow would have put it.
            leftSpacer: Math.max(0, Math.round(textWidth - radius)),
            topMargin: DISMISS_GAP + radius,
        })
    }

    createStop = (
        tutorialType: TutorialType,
        lines: Graphics,
        container: DyanmicTextContainer,
        throbber: Throbber,
        releaseHover: Callback<void>,
    ): Callback<void> => {
        // Idempotent: a clue can be taken down by its own dismiss button, by its expiry timer, by the
        // panel it points at being rebuilt, and by the tutorial being marked seen -- and more than one
        // of those can land on the same clue.
        let stopped = false
        const stop = () => {
            if (stopped) {
                return
            }
            stopped = true
            // Give the world its mouse back FIRST. The panel takes it away while the pointer is on it
            // (see setupTutorial), and the commonest way for one to go is the player clicking the
            // panel's own dismiss button -- with the pointer right there on it. Removing a display
            // object does not make pixi deliver the mouseout it owed, so the latch would stay set and
            // the player would be left unable to walk or swing until the next room load. The expiry
            // timer and a panel rebuild can land under a resting pointer the same way.
            releaseHover()
            throbber.stop()
            // Was hardcoded to "controlbar", so a dismissed hotbar/map popup stayed in the set and
            // could never be raised again in the same session.
            this.activeTutorials.delete(tutorialType)
            // A rebuilt or resized control must not leave a fading outline at its old location.
            if (isEmberClueType(tutorialType)) {
                container.parent?.removeChild(container)
                lines.parent?.removeChild(lines)
                return
            }
            new Fader(container, {
                releaseCallback: () => {
                    container.parent?.removeChild(container)
                },
            }).update()
            new Fader(lines, {
                releaseCallback: () => {
                    lines?.parent?.removeChild(lines)
                },
            }).update()
        }

        return stop
    }

    // Builds the box + blinking pointer every tutorial and clue is made of. `timeoutMs` of 0 means it
    // does not take itself down: the ember clues have their own lifetime (see ember_clues.ts).
    setupTutorial = (
        tutorialType: TutorialType,
        text: string[],
        attachToSurface: boolean = true,
        timeoutMs: number = POPUP_TIMEOUT_MS,
    ) => {
        if (this.activeTutorials.has(tutorialType)) {
            return
        }
        this.activeTutorials.add(tutorialType)

        // Solid bodies keep overlapping context panels readable over both the world and other UI.
        const isClue = isEmberClueType(tutorialType)
        const container = new DyanmicTextContainer({
            defaultTextRole: "body",
            backdrop: true,
            opaque: true,
            // A subdued red rim ties clues to their pointer without overpowering the copy.
            borderColor: isClue ? 0x995451 : undefined,
            margin: 4,
        })
        // Filter only the backdrop: text and the close control stay crisp, and shadow padding
        // does not change the panel bounds used to place clues and join their leaders.
        container.backdrop.filters = [new DropShadowFilter({
            color: 0x000000,
            alpha: 0.7,
            blur: 4,
            distance: 3,
            rotation: 90,
            quality: 3,
        })]
        const lines = new Graphics()
        if (attachToSurface) {
            this.surface.addChild(lines)
        }

        const throbber = new Throbber(lines, {
            blinkInterval: 250,
            minAlpha: 0.5,
        })

        // While the pointer is on the panel the world does not get the mouse, so a click meant for
        // the box (or for its dismiss button) is not also a swing at whatever is behind it. Tracked
        // here rather than left to pixi so the teardown can hand the mouse back (see createStop),
        // and so a panel never clears a latch it did not set.
        let hovered = false
        const setHovered = (over: boolean) => {
            if (hovered === over) {
                return
            }
            hovered = over
            this.gui.mouseOverControl(over)
        }

        container.interactive = true
        container.on("mouseover", () => setHovered(true))
        container.on("mouseout", () => setHovered(false))

        const stop = this.createStop(tutorialType, lines, container, throbber, () => setHovered(false))

        text.forEach((l, index) => container.append2(l, {
            color: 0xfff3e0,
            topMargin: index === 0 ? 0 : 2,
        }))

        // Dismissing a CLUE has to disarm it as well as take it down, or the next rebuild of the panel
        // it points at would put it straight back up over a player who has said they are done with it.
        this.addDismiss(container, () => {
            stop()
            if (isEmberClueType(tutorialType)) {
                this.clues.disarm(tutorialType)
            }
        })

        throbber.update()

        if (attachToSurface) {
            this.surface.addChild(container)
        }

        if (timeoutMs > 0) {
            setTimeout(stop, timeoutMs)
        }

        return { lines, container, stop }
    }

    setupHotbarTutorial = () => {
        const tutorial = this.setupTutorial("hotbar", [
            "This is your hotbar. Activate readied equipment, supplies, and follower",
            "commands by pressing their associated numbers and letters (1, 2, J, K ...).",
        ])
        if (!tutorial) {
            return
        }
        const { lines, container } = tutorial
        const anchor = this.builtInClueAnchor("ember-ready")
        if (!anchor) return
        anchor.host.addChild(lines, container)
        container.x = anchor.x
        container.y = anchor.y - container.height - 45
        lines.x = anchor.x - 4
        lines.y = anchor.y - 4

        lines.lineStyle({
            width: 2,
            color: 0xff0000,
            alpha: 1.0,
        })
        lines.drawRect(anchor.w / 2 + 4, -25, 0.5, 25)
        lines.drawRect(0, 0, anchor.w + 8, anchor.h + 8)
    }

    setupMapTutorial = () => {
        const tutorial = this.setupTutorial("map", [
            "This your map. Points of interest appear as icons, and new ones",
            "will pulse to get your attention!",
        ])
        if (!tutorial) {
            return
        }
        const { lines, container } = tutorial

        container.x = this.minimapSurface.x - container.width
        container.y = this.minimapSurface.y - container.height - 55

        lines.x = container.x + container.width + 50
        lines.y = container.y + container.height / 2 - 25
        lines.lineStyle({
            width: 2,
            color: 0xff0000,
            alpha: 1.0,
        })
        lines.drawRect(-60, 0, 60, 0.5)
        lines.drawRect(0, 0, 0.5, 108)

        lines.drawRect(-60, 110, this.minimapSurface.width + 15, this.minimapSurface.height + 15)
    }

    setupControlbarTutorial = () => {
        const tutorial = this.setupTutorial("controlbar", [
            "Access your inventory and craft items using these controls.",
        ])

        if (!tutorial) {
            return
        }

        const { lines, container } = tutorial
        const anchor = this.builtInClueAnchor("ember-items")
        if (!anchor) return
        anchor.host.addChild(lines, container)
        container.x = anchor.x - container.width + 10
        container.y = anchor.y + anchor.h + 10
        lines.x = anchor.x
        lines.y = anchor.y

        lines.lineStyle({
            width: 2,
            color: 0xff0000,
            alpha: 1.0,
        })
        lines.drawRect(0, 0, anchor.w, anchor.h)
        lines.drawRect(anchor.w - 1, anchor.h, 1, 10)
        lines.drawRect(anchor.w - 1, anchor.h + 10, -50, 1)
    }

    setupCompanionTutorial = () => {
        const playerEntity = this.gui.logic.playerEntity
        if (!playerEntity) {
            this.activeTutorials.delete("companion")
            return
        }

        const companion = this.gui.logic.entityManager.find(e => e.leaderEntityId === playerEntity.entityId)
        if (!companion) {
            this.activeTutorials.delete("companion")
            return
        }

        const entityRenderable = this.gui.logic.clientRenderer.map().overlay().getEntityRenderable(companion.entityId)
        if (!entityRenderable) {
            this.activeTutorials.delete("companion")
            return
        }

        const tutorial = this.setupTutorial(
            "companion",
            ["This is your companion and protected. Mouse over", "him, and click the icons that appear to interact."],
            false,
        )
        if (!tutorial) {
            return
        }
        const { lines, container } = tutorial

        // container.x = this.minimapSurface.x - container.width
        container.x = -container.width
        container.y = -25

        entityRenderable.addChild(lines)
        entityRenderable.addChild(container)

        lines.x = container.x + container.width
        lines.y = container.y + container.height / 2

        lines.lineStyle({
            width: 2,
            color: 0xff0000,
            alpha: 1.0,
        })
        lines.drawRect(-10, 0, 30, 0.5)
        lines.drawRect(20, 0, 0.5, 25)
    }

    // =========================================================================================
    // THE EMBER CLUES
    //
    // Five pointer-and-text boxes that name the controls the camp's first fire is built with. They are
    // armed by the SERVER (assets/scripts/v2/ember-tutorial.js), one beat at a time, because only the
    // director knows which beat is live -- the client cannot tell "waiting for a campfire to be
    // crafted" from "the player is browsing recipes for fun".
    //
    // The rules (armed vs anchored, lifetime, teardown) are in ember_clues.ts. This half draws them.
    // =========================================================================================

    // Whatever draws one of the clued controls says where it landed. Called again on every rebuild of
    // the panel it lives in.
    registerClueAnchor = (tutorialType: EmberClueType, anchor: ClueAnchor) =>
        this.clues.registerAnchor(tutorialType, anchor)

    // The control has gone (its panel closed, or this rebuild no longer draws it).
    clearClueAnchor = (tutorialType: EmberClueType) => this.clues.clearAnchor(tutorialType)

    // The player DID the thing this clue was pointing at, so it has served its purpose and goes for
    // good. Disarm rather than merely retract: a satisfied clue must not come back when the panel it
    // pointed at is next rebuilt, or reopening the pack would re-light a label for a step already done.
    satisfyClue = (tutorialType: EmberClueType) => this.clues.disarm(tutorialType)

    // Every clue down and disarmed: the tutorial is over, or the camp is.
    clearEmberClues = () => this.clues.clearAll()

    isClueArmed = (tutorialType: EmberClueType): boolean => this.clues.isArmed(tutorialType)

    isClueShowing = (tutorialType: EmberClueType): boolean => this.clues.isShowing(tutorialType)

    // Draw one clue at its anchor, and hand back how to take it down.
    private drawClue = (tutorialType: EmberClueType, anchor: ClueAnchor): Callback<void> | undefined => {
        const tutorial = this.setupTutorial(tutorialType, CLUE_TEXT[tutorialType], false, 0)
        if (!tutorial) {
            return undefined
        }
        const { lines, container, stop } = tutorial
        anchor.host.addChild(lines)
        anchor.host.addChild(container)

        const placement = anchor.placement || "left"
        const { boxX, boxY, leader } = clueBoxLayout({
            anchorX: anchor.x,
            anchorY: anchor.y,
            anchorW: anchor.w,
            anchorH: anchor.h,
            boxW: container.width,
            boxH: container.height,
            boxMinX: anchor.boxMinX,
            placement,
        })
        container.x = boxX
        container.y = boxY

        // clueBoxLayout works in content sizes, but a container's ORIGIN is not its top-left corner:
        // the backdrop is drawn from -5-margin, so what is on screen starts above and to the left of
        // where the box was placed. Re-place against the bounds that actually exist, so the box really
        // clears its control and the leader comes out straight instead of stepping.
        const bounds = container.getLocalBounds()
        if (placement === "above" || placement === "below") {
            // Sit its visible edge clear above or below the control, and centre it on the control --
            // but never past the host's left edge. clueBoxLayout already clamps, and re-placing against
            // the real bounds threw that away: the ready bar is in the bottom-left corner, so a box
            // centred on it ran off the side of the screen with half its words unreadable.
            //
            // Clamped on the VISIBLE edge (container.x + bounds.x), not the origin, because the
            // backdrop is drawn outside the content box.
            const minX = anchor.boxMinX ?? CLUE_BOX_MIN_X
            const centred = anchor.x + anchor.w / 2 - (bounds.x + bounds.width / 2)
            container.x = Math.round(Math.max(minX - bounds.x, centred))
            container.y = placement === "above"
                ? Math.round(anchor.y - CLUE_LEADER_GAP - (bounds.y + bounds.height))
                : Math.round(anchor.y + anchor.h + CLUE_LEADER_GAP - bounds.y)
        } else {
            container.y = Math.max(
                CLUE_BOX_MIN_Y - bounds.y,
                anchor.y + anchor.h / 2 - (bounds.y + bounds.height / 2),
            )
        }

        // Clamp the visible box in the same coordinates as its scaled/mobile host.
        const clampBox = (value: number, min: number | undefined, max: number | undefined, offset: number, size: number) => {
            const lower = min === undefined ? -Infinity : min - offset
            const upper = max === undefined ? Infinity : max - offset - size
            return Math.max(lower, Math.min(value, Math.max(lower, upper)))
        }
        container.x = clampBox(container.x, anchor.boxMinX, anchor.boxMaxX, bounds.x, bounds.width)
        container.y = clampBox(container.y, anchor.boxMinY, anchor.boxMaxY, bounds.y, bounds.height)

        lines.x = anchor.x
        lines.y = anchor.y
        lines.lineStyle({
            width: 2,
            color: CLUE_RED,
            alpha: 1.0,
        })
        // A ring round the control itself...
        lines.drawRect(0, 0, anchor.w, anchor.h)

        // ...and a leader back to the box, when the clamp left room for one.
        //
        // Drawn from the panel's VISIBLE edge, taken off its real bounds rather than off container.width:
        // the backdrop is a rounded rect deliberately larger than its contents (see
        // DyanmicTextContainer), so the box on screen is wider than the text it wraps, and measuring
        // the text left the line ending short of the panel it was supposed to be joined to.
        //
        // The elbow is only for the one case the re-centring above cannot fix: a box tall enough that
        // levelling it with its control would push it off the top of the screen, where it is clamped
        // instead and a straight line would leave the panel at a height the panel no longer occupies.
        // Every other time the two ends already line up and this draws one straight line.
        if (placement === "above" && container.y + bounds.y + bounds.height < anchor.y) {
            // Straight down from the box's visible bottom to the top of the control, on the control's
            // own centre line.
            const x = anchor.w / 2
            const startY = container.y + bounds.y + bounds.height - anchor.y
            lines.moveTo(x, startY - LEADER_OVERLAP)
            lines.lineTo(x, 0)
        } else if (placement === "below" && container.y + bounds.y > anchor.y + anchor.h) {
            const x = anchor.w / 2
            const startY = container.y + bounds.y - anchor.y
            lines.moveTo(x, startY + LEADER_OVERLAP)
            lines.lineTo(x, anchor.h)
        } else if (placement === "left" && leader > 0) {
            const startX = container.x + bounds.x + bounds.width - anchor.x
            const startY = container.y + bounds.y + bounds.height / 2 - anchor.y
            const endY = anchor.h / 2
            // Start a little INSIDE the panel so there can be no seam where the two meet.
            lines.moveTo(startX - LEADER_OVERLAP, startY)
            if (Math.round(startY) !== Math.round(endY)) {
                const elbowX = startX / 2
                lines.lineTo(elbowX, startY)
                lines.lineTo(elbowX, endY)
            }
            lines.lineTo(0, endY)
        }

        return stop
    }

    // The one clue whose control is part of the permanent HUD rather than a panel, so it needs no
    // registration: in a CAMP the pack is opened from the ITEMS chip in the top-right row, because
    // ember rooms suppress the control bar (GuiManager.roomUpdated). The bar's own inventory.png --
    // the first entry in ControlBar.setup() -- is the fallback for a room that leaves the bar up.
    private builtInClueAnchor = (tutorialType: EmberClueType): ClueAnchor | null => {
        // The ready bar, which is the equipped widget: permanent HUD, so nothing has to report it.
        if (tutorialType === "ember-ready" || tutorialType === "ember-wood") {
            if (!this.equippedWidget || !this.equippedWidget.visible) {
                return null
            }
            const bounds = tutorialType === "ember-wood"
                ? this.equippedWidget.getWoodSlotBounds?.()
                : this.equippedWidget.getLocalBounds()
            if (!bounds) return null
            const host = this.equippedWidget.parent || this.surface
            return {
                host,
                ...this.viewportLimits(host),
                x: this.equippedWidget.x + bounds.x * this.equippedWidget.scale.x,
                y: this.equippedWidget.y + bounds.y * this.equippedWidget.scale.y,
                w: bounds.width * this.equippedWidget.scale.x,
                h: bounds.height * this.equippedWidget.scale.y,
                placement: "above",
            }
        }
        if (tutorialType !== "ember-items" && tutorialType !== "ember-wood-items") {
            return null
        }
        const chip = this.emberInventoryChip
        if (chip && chip.visible && (!chip.parent || chip.parent.worldVisible)) {
            const host = chip.parent || this.surface
            return { host, ...this.viewportLimits(host), x: chip.x, y: chip.y, w: chip.getWidth(), h: chip.getHeight() }
        }
        if (!this.controlBar || !this.controlBar.visible) {
            return null
        }
        return {
            host: this.surface,
            x: this.controlBar.x + CONTROLBAR_ITEMS_OFFSET_X,
            y: this.controlBar.y,
            w: CONTROLBAR_ITEMS_W,
            h: CONTROLBAR_ITEMS_H,
        }
    }

    private viewportLimits = (host: Container): Partial<ClueAnchor> => {
        if (!this.gui.stage) return {}
        const safe = getUiMetrics().safeViewport
        const topLeft = host.toLocal(new Point(safe.left, safe.top), this.gui.stage)
        const bottomRight = host.toLocal(new Point(safe.right, safe.bottom), this.gui.stage)
        return { boxMinX: topLeft.x + 8, boxMaxX: bottomRight.x - 8,
            boxMinY: topLeft.y + 8, boxMaxY: bottomRight.y - 8 }
    }

    private hudAnchors = new Map<EmberClueType, ClueAnchor>()
    refreshHudAnchors = () => {
        for (const type of ["ember-ready", "ember-wood", "ember-items", "ember-wood-items"] as EmberClueType[]) {
            const anchor = this.builtInClueAnchor(type)
            const previous = this.hudAnchors.get(type)
            if (!anchor) {
                this.clues.clearAnchor(type)
                this.hudAnchors.delete(type)
            } else if (!previous || Object.keys(anchor).some(key => anchor[key] !== previous[key])) {
                this.hudAnchors.set(type, anchor)
                this.clues.registerAnchor(type, anchor)
            }
        }
    }

    // The one way in from the wire (GuiManager.showTutorial). What each kind of message means -- and
    // which of them `suppressed` is allowed to swallow -- is decided in one place, routeTutorial.
    setup = (tutorialType: TutorialType | null, timeoutMs?: number) => {
        this.refreshHudAnchors()
        const route = routeTutorial(tutorialType, this.suppressed)
        if (route === "clear") {
            // Step changes leave the inventory open; its controls still have valid anchors.
            // Room changes use clearEmberClues() to discard those anchors as well.
            this.clues.clearAll(true)
            return
        }
        if (route === "suppressed") {
            return
        }
        if (route === "clue") {
            this.clues.arm(tutorialType as EmberClueType, timeoutMs)
            return
        }
        const all = false
        if (all || tutorialType === "controlbar") {
            this.setupControlbarTutorial()
        }
        if (all || tutorialType === "hotbar") {
            this.setupHotbarTutorial()
        }
        if (all || tutorialType === "map") {
            this.setupMapTutorial()
        }
        if (all || tutorialType === "companion") {
            setTimeout(() => {
                this.setupCompanionTutorial()
            }, 4000)
        }
    }
}
